'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { TypeAnimation } from 'react-type-animation'

// Equivalent to Rail (#) -> Structure/Architecture
// Bed-double inspired Icon (Stay)
const StayIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" />
    <path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4" />
    <path d="M12 4v6" />
    <path d="M2 14h20" />
  </svg>
)

// Crown inspired Icon (Elegance)
const EleganceIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7Z" />
  </svg>
)

// Concierge-bell inspired Icon (Welcome)
const WelcomeIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 18h20" />
    <path d="M20 16a8 8 0 1 0-16 0" />
    <path d="M12 4v4" />
    <path d="M10 4h4" />
  </svg>
)

const MODES = [
  { id: 'stay', component: StayIcon },
  { id: 'elegance', component: EleganceIcon },
  { id: 'welcome', component: WelcomeIcon },
  { id: 'typing', component: null },
]

interface XsareLogoProps {
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showSlogan?: boolean
}

export function XsareLogo({ className = '', size = 'md', showSlogan = false }: XsareLogoProps) {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    const intervals = [3000, 3000, 3000, 6000]
    const timer = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % MODES.length)
    }, intervals[currentIndex])
    return () => clearTimeout(timer)
  }, [currentIndex])

  const currentMode = MODES[currentIndex]

  const sizeClasses = {
    sm: 'text-xl',
    md: 'text-3xl',
    lg: 'text-5xl',
    xl: 'text-7xl',
  }

  const iconSizes = {
    sm: 20,
    md: 28,
    lg: 44,
    xl: 60,
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className={`flex items-center gap-2 font-black tracking-tighter ${sizeClasses[size]}`}>
        <span>X</span>

        <div className="relative inline-flex items-center justify-center min-h-[1em] min-w-[1em]">
          <AnimatePresence mode="wait">
            {currentMode.id === 'typing' ? (
              <motion.span
                key="typing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-foreground text-[0.75em] whitespace-nowrap"
              >
                <TypeAnimation
                  sequence={[
                    'Sare', 1500,
                    ' by Djotta', 1500,
                  ]}
                  wrapper="span"
                  cursor={false}
                  repeat={0}
                />
              </motion.span>
            ) : (
              <motion.div
                key={currentMode.id}
                initial={{ scale: 0.5, opacity: 0, rotate: -180 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.5, opacity: 0, rotate: 180 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="inline-flex items-center justify-center text-foreground"
              >
                {currentMode.component && <currentMode.component size={iconSizes[size]} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {currentMode.id !== 'typing' && (
          <span className="text-foreground text-[0.75em] opacity-0 pointer-events-none select-none whitespace-nowrap">
            By Djotta
          </span>
        )}
      </div>

      {showSlogan && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground mt-1 ml-1"
        >
          Hotel Intelligence System
        </motion.p>
      )}
    </div>
  )
}
